import { Button, lighten, Theme, Tooltip, Typography } from '@mui/material'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { CSSProperties, useCallback, useState } from 'react'
import { IssueCode } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { projectSelectors } from '../store/selectors/projectSelectors'
import { useBomItemThumbnailImageSrc } from '../useBomItemThumbnailImageSrc'
import { useBomItemIssueDescription } from './useBomItemIssueDescription'

type RowThumbnailButtonSizes = 'small' | 'medium' | 'large'

type BomItemThumbnailButtonProps = {
  bomItemPointer: BomItemPointer
  threeJSContainerStyle?: CSSProperties
  show2dModel?: boolean
  withCameraControls?: boolean
  showToolbox?: boolean
  size?: RowThumbnailButtonSizes
  showTooltip?: boolean
  pictureClassName?: string
  disableClick?: boolean
  operationPending?: boolean
  dontRenderEmpty?: boolean
  dataTestId?: string
}

const useStyles = makeStyles<{
  size?: RowThumbnailButtonSizes
  disableClick?: boolean
  operationPending?: boolean
}>({ name: 'BomItemThumbnailButton' })((theme: Theme, props) => {
  let width = theme.spacing(6)

  if (props.size === 'small') width = theme.spacing(4)
  if (props.size === 'large') width = theme.spacing(6)

  return {
    imgSize: {
      width: width,
      height: width,
      flexShrink: 0,
      flexGrow: 0,
    },
    thumbnailSize: {
      width: theme.spacing(30),
    },
    imgButton: {
      padding: 0,
      zIndex: 0,
      backgroundColor: props.operationPending
        ? lighten(theme.palette.action.disabled, 0.825)
        : 'inherit',
      mixBlendMode: props.operationPending
        ? 'multiply'
        : (theme?.BoMThumbnail?.mixBlendMode as any),
      // minWidth: width,
      minWidth: 0,
      minHeight: '42px',
      border: 'none !important',
    },
    thumbnail: {
      padding: theme.spacing(1),
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
    },

    tooltipArrow: {
      '&:before': {
        backgroundColor: theme.palette.action.active,
      },
    },
  }
})

const RowThumbnailButton = (props: BomItemThumbnailButtonProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const { classes } = useStyles({
    size: props.size ? props.size : 'medium',
    disableClick: props.disableClick ? props.disableClick : false,
    operationPending: props.operationPending ? props.operationPending : false,
  })

  // const openDialogByURL = useURLDialogManager()

  const openDetailsDialog = useBomItemDetailsDialog()

  const upperSideTodo = useAppSelector(
    projectSelectors.bomItemIssueByCodeSelector(
      props.bomItemPointer,
      IssueCode.UpperSideNotSelected,
    ),
  )

  const { getIssueDescription } = useBomItemIssueDescription()

  const setDetailsOpen = useCallback((e) => {
    e.stopPropagation()

    openDetailsDialog('models', {
      bomItemPointer: props.bomItemPointer,
    })
    // openDialogByURL('Drawings', {
    //   bomItemId: props.bomItemPointer.id,
    //   bomItemType: props.bomItemPointer.type,
    //   open: true,
    //   initialTab: 'models',
    // } as DrawingsDialogProps)
  }, [openDetailsDialog, props.bomItemPointer])

  const handleOpenTooltip = useCallback(() => {
    setTooltipOpen(true)
  }, [])

  const handleCloseTooltip = useCallback(() => {
    setTooltipOpen(false)
  }, [])

  const { imageSrc, thumbnailFilename } = useBomItemThumbnailImageSrc(
    props.bomItemPointer,
    props.show2dModel,
  )

  if (!thumbnailFilename) {
    if (props.dontRenderEmpty) return null
    return <div className={classes.imgSize} />
  }

  const button = (
    <Button
      onClick={setDetailsOpen}
      disableFocusRipple
      disableTouchRipple
      disableRipple
      disableElevation
      variant="text"
      className={`${props.pictureClassName} ${classes.imgButton}`}
      data-testid={props.dataTestId}
    >
      <img
        src={imageSrc}
        loading="lazy"
        decoding="async"
        className={classes.imgSize}
        alt={thumbnailFilename}
      />
    </Button>
  )

  if (props.showTooltip) {
    return (
      <Tooltip
        open={tooltipOpen}
        onOpen={handleOpenTooltip}
        onClose={handleCloseTooltip}
        placement={'top'}
        arrow
        enterDelay={500}
        enterNextDelay={500}
        title={
          tooltipOpen ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                loading="lazy"
                decoding="async"
                src={imageSrc}
                alt={thumbnailFilename}
                className={classes.thumbnailSize}
                style={{ display: 'block' }}
              />
              {upperSideTodo && (
                <Typography variant="caption" color="textPrimary">
                  {getIssueDescription(upperSideTodo)}
                </Typography>
              )}
            </div>
          ) : ''
        }
        slotProps={{
          tooltip: {
            className: classes.thumbnail,
          },
          arrow: {
            className: classes.tooltipArrow,
          },
          popper: {
            popperOptions: {
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top', 'bottom'],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 8,
                    boundary: document.getElementById(props.bomItemPointer.id),
                  },
                },
              ],
            },
          },
        }}
      >
        {button}
      </Tooltip>
    )
  }

  return (
    <Button
      onClick={setDetailsOpen}
      disableFocusRipple
      disableTouchRipple
      disableRipple
      disableElevation
      disabled={props.disableClick}
      className={props.pictureClassName}
      data-testid={props.dataTestId}
    >
      <img
        loading="lazy"
        decoding="async"
        src={imageSrc}
        className={classes.imgSize}
        alt={thumbnailFilename}
      />
    </Button>
  )
}

export { RowThumbnailButton }
